import React from 'react';
import { Field } from 'react-final-form';
import classnames from 'classnames';
import styles from './FormFields.module.scss';

interface ValidatedInputHyperlinksProps {
  labelComponent?: React.ReactNode;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  validate?: (value: any) => string | undefined;
  name: string;
  placeholder?: string;
  type?: string;
  component?: React.ComponentType<any> | string;
  inputProps?: Record<string, any> & { pattern?: string };
  errorVisible?: boolean;
  showErrorBeforeTouched?: boolean;
  errorClassName?: string;
  changeCursorPosition: (position: number, selection?: { selectionStart: number; selectionEnd: number; selectedText: string }) => void;
  formProps?: any;
  [key: string]: any;
}

class ValidatedInputHyperlinks extends React.Component<ValidatedInputHyperlinksProps> {
  static defaultProps: Partial<ValidatedInputHyperlinksProps> = {
    component: 'input'
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>, input: any) => {
    // check the validity of the input in the case a "pattern" prop is passed in inputProps
    // if valid, call react-final-form's onChange function like normal
    // this is so the pattern regex is checked every time the user enters a value
    // example usage in AddBank.js
    if (this.props.inputProps && this.props.inputProps.pattern) {
      if (e.target.validity.valid) {
        input.onChange(e);
      }
    } else {
      // default behaviour
      input.onChange(e);
    }
  };

  handleCursorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Get cursor position
    const cursorPosition = e.target.selectionStart;
    this.props.changeCursorPosition(cursorPosition);

    // Get selection range
    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;

    // Check if any text is selected
    if (selectionStart !== selectionEnd) {
      const selectedText = e.target.value.substring(
        selectionStart,
        selectionEnd
      );
      this.props.changeCursorPosition(cursorPosition, {
        selectionStart,
        selectionEnd,
        selectedText
      });
    } else {
      this.props.changeCursorPosition(cursorPosition);
    }
  };

  render() {
    const {
      labelComponent,
      label,
      className,
      style,
      validate,
      name,
      placeholder,
      type,
      component: Component,
      inputProps,
      errorVisible,
      showErrorBeforeTouched,
      errorClassName,
      ...rest
    } = this.props;

    const labelContent = labelComponent || (label && <div>{label}</div>);

    const showError = errorVisible === undefined ? true : errorVisible;
    return (
      <Field
        className={styles.fieldContainer}
        validate={validate}
        name={name}
        {...rest}
        render={({ meta, input }) => (
          <div className={styles.fieldContainer}>
            <div
              className={classnames(styles.field, {
                [styles.hasError]:
                  showError &&
                  meta.error !== undefined &&
                  (meta.touched || showErrorBeforeTouched)
              })}
            >
              {labelContent}
              <Component
                className={classnames(className)}
                style={style}
                placeholder={placeholder}
                type={type}
                {...input}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleChange(e, input)} // overriding onChange in {...input}
                {...inputProps}
                {...(rest.formProps && {
                  formProps: rest.formProps
                })}
              />
            </div>
            {showError &&
              meta.error !== undefined &&
              (meta.touched || showErrorBeforeTouched) && (
                <div
                  className={classnames(
                    styles.error,
                    'form-error',
                    errorClassName
                  )}
                  data-test={`error-${name}`}
                >
                  {meta.error}
                </div>
              )}
          </div>
        )}
      />
    );
  }
}

export default ValidatedInputHyperlinks;
